import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, FileDown, ExternalLink, Users, Download as DownloadIcon, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_MAP } from "@/data/categories";

export default function DownloadsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/downloads")
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="bg-white border-2 border-slate-950 p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={DownloadIcon} label="Descargas totales" value={data.total} testId="downloads-total" />
        <SummaryCard icon={Users} label="Visitantes únicos" value={data.unique_visitors} sub="(por IP)" testId="downloads-unique" />
        <SummaryCard icon={TrendingUp} label="Productos descargados" value={data.top_products.length} testId="downloads-products" />
      </div>

      {/* Top products */}
      <div className="bg-white border-2 border-slate-950">
        <div className="border-b-2 border-slate-950 p-4 md:p-6">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Ranking</div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Productos más descargados</h2>
        </div>

        {data.top_products.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            <FileDown className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="font-display uppercase tracking-tight">Sin descargas aún</p>
            <p className="text-sm mt-1">Los visitantes verán este registro cuando descarguen una ficha PDF.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b-2 border-slate-950">
                <tr className="text-left font-display font-bold uppercase text-xs tracking-wider">
                  <th className="p-4 w-12">#</th>
                  <th className="p-4">Producto</th>
                  <th className="p-4">Categoría</th>
                  <th className="p-4 text-right">Descargas</th>
                  <th className="p-4 text-right">Visitantes únicos</th>
                  <th className="p-4">Última descarga</th>
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p, idx) => (
                  <tr key={p.product_id} data-testid={`top-product-${p.product_id}`} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-4 font-display font-black text-amber-600">{String(idx + 1).padStart(2, "0")}</td>
                    <td className="p-4">
                      <Link to={`/producto/${p.product_id}`} target="_blank" className="font-display font-bold text-slate-950 hover:text-amber-600 inline-flex items-center gap-1">
                        {p.product_name} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                    <td className="p-4 font-mono text-xs uppercase tracking-wider text-slate-600">
                      {CATEGORY_MAP[p.category]?.name || p.category}
                    </td>
                    <td className="p-4 text-right font-display font-black text-lg">{p.count}</td>
                    <td className="p-4 text-right font-mono text-slate-700">{p.unique_visitors}</td>
                    <td className="p-4 font-mono text-xs text-slate-500">{formatDate(p.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent log */}
      <div className="bg-white border-2 border-slate-950">
        <div className="border-b-2 border-slate-950 p-4 md:p-6">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Actividad</div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Registro reciente</h2>
        </div>
        {data.recent.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Sin actividad reciente.</div>
        ) : (
          <ul className="divide-y divide-slate-200 max-h-[480px] overflow-y-auto">
            {data.recent.map((r) => (
              <li key={r.id} data-testid={`download-log-${r.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400 md:w-44 shrink-0">
                  {formatDate(r.created_at)}
                </span>
                <Link to={`/producto/${r.product_id}`} target="_blank" className="font-display font-bold uppercase text-sm tracking-tight text-slate-950 hover:text-amber-600 flex-1">
                  {r.product_name || "Producto eliminado"}
                </Link>
                <span className="font-mono text-xs text-slate-500 md:w-32 truncate" title={r.ip}>{r.ip}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400 md:w-40 truncate" title={r.user_agent}>
                  {shortUA(r.user_agent)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-white border-2 border-slate-950 p-5">
      <Icon className="w-5 h-5 text-amber-500" />
      <div className="font-display font-black text-4xl mt-2 tracking-tight">{value}</div>
      <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-[11px] font-mono text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("es-CL"); } catch { return iso; }
}

function shortUA(ua = "") {
  if (!ua) return "—";
  if (/iPhone|iPad/i.test(ua)) return "iOS / Safari";
  if (/Android/i.test(ua)) return "Android";
  if (/Edg/i.test(ua)) return "Edge";
  if (/Chrome/i.test(ua)) return "Chrome";
  if (/Firefox/i.test(ua)) return "Firefox";
  if (/Safari/i.test(ua)) return "Safari";
  return ua.slice(0, 30);
}
